import React from 'react';
import {Icon, Popup} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {
    get as _get,
    intersectionBy as _intersectionBy,
    sortBy as _sortBy,
    isEqual as _isEqual,
} from 'lodash';

import {convertToInt} from '@utils/helpers';
import Link from '@appComponents/Link';
import Form from '@appComponents/ReduxFormControls';
import {clipConfigOptions} from '@graphql/clipConfiguration/query';
import {extractEventContentTypes} from '@modules/clipConfiguration/utils/forms/common/formHelpers';
import {EVENT_TYPE_STAGE} from '@constants/variables';

export class renderPropertyLicenceField extends React.Component{
    static propTypes = {
        input: PropTypes.object,
        defaultValue: PropTypes.string,
        options: PropTypes.array,
        disabled: PropTypes.bool,
        formValues: PropTypes.shape({
            eventStageType: PropTypes.number,
        }),
    };

    constructor(props) {
        super(props);

        let value = props.input.value ? props.input.value : props.defaultValue;

        this.state = {
            value: value,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (1 === nextProps.options.length && !nextProps.input.value) {
            let value = nextProps.options[0].value,
                onChangeCallback = _get(nextProps, 'onChangeSelect', () => {});

            !nextProps.disabled && onChangeCallback(null, {name: nextProps.input.name, value: value});

            return {
                value: value,
            };

        } else if (nextProps.defaultValue !== nextProps.input.value) {
            const newValue = nextProps.formValues.eventStageType === EVENT_TYPE_STAGE
                ? convertToInt(nextProps.defaultValue)
                : nextProps.defaultValue;

            if (newValue === prevState.value) {
                return null;
            }

            return {value: newValue};
        }

        return null;
    }

    render() {
        let value = (this.props.input.value || this.state.value);

        return  <div className='formRow --propertyLicenceRowInput'>
            <Form.SemanticSelect {...this.props} defaultValue={value} type='select'/>
            { value && <Popup
                content='See the property licence'
                position='right center'
                trigger={(
                    <span className='link'>
                        <Link name='propertyLicences.edit' newWindow={true} params={{id: value}}>
                            <Icon name='external'/>
                        </Link>
                    </span>
                )}
            />
            }
        </div>;
    }
}

export function renderEventPresetDropdown(data) {
    let selectProps = {...data};

    selectProps.defaultValue = null;

    let options = _sortBy(data.options, (item) => item.text.toLocaleLowerCase());

    return (
        <div className='formRow --propertyLicenceRowInput'>
            <Form.SemanticSelect {...selectProps} options={options} type='select'/>
            { 0 < data.options.length && <Popup
                content='See the list of event presets for the selected property licence'
                position='right center'
                trigger={(
                    <span className='link'>
                        <Link name='propertyLicences.eventPresets.index' newWindow={true} params={{
                            id: _get(data, 'formValues.property_licence', ''),
                        }}>
                            <Icon name='external'/>
                        </Link>
                    </span>
                )}
            />
            }
        </div>
    );
}

export function prefillCoverage(isCoverageFieldHidden, presetProducts, coverageOptions, setField, commonProducts, updateState) {
    if (!isCoverageFieldHidden) {
        const nextCommonProducts = _intersectionBy(coverageOptions, presetProducts, product => convertToInt(product.id));

        if (JSON.stringify(nextCommonProducts) !== JSON.stringify(commonProducts)) {
            updateState({ commonProducts: nextCommonProducts });

            setField('coverage', {
                defaultValue: 0 < nextCommonProducts.length ? nextCommonProducts.map(product => convertToInt(product.value)) : [],
                timestamp: Date.now(),
            });
        }
    }
}

export const getCoverageValuesFromPresetProducts = (coverageOptions = [], presetProducts = []) => {
    const selectedPresetProductIds = (presetProducts || []).map((product) => Number(product.id));

    return (coverageOptions || [])
        .filter((option) => selectedPresetProductIds.includes(Number(option.id ?? option.value)))
        .map((option) => Number(option.value ?? option.id))
        .filter((coverageId) => !Number.isNaN(coverageId));
};

export const transformProductsForCoverageDropdown = (products) => {
    return products.map(product => ({
        key: product.id,
        value: product.id,
        text: product.short_name,
        id: product.id,
        name: product.short_name,
    }));
};

export const getClipProviderFromPropertyLicence = (rightScopes, products) => {
    for (const event of rightScopes) {
        const scopes = event.right_scopes_for_event || event.right_scopes_for_match;

        if (scopes) {
            for (const scope of scopes) {
                if (scope.clip_provider) {
                    const hasMatchingProduct = scope.usages.some(usage =>
                        products.some(product => product.id === usage.product.id)
                    );

                    if (hasMatchingProduct) {
                        return scope.clip_provider.id;
                    }
                }
            }
        }
    }

    return null;
};

export const getClipProvidersDefaultsFromEvent = (event = {}) => {
    return Object.fromEntries(
        (event?.clip_providers ?? []).map(({event_content_type, clip_provider}) => [
            String(event_content_type?.id),
            clip_provider?.id ? String(clip_provider.id) : '',
        ])
    );
};

export const normalizeClipProvidersValues = (clipProviders = {}) => {
    return Object.fromEntries(
        Object.entries(clipProviders).map(([eventContentTypeId, clipProviderId]) => [
            String(eventContentTypeId),
            clipProviderId ? String(clipProviderId) : '',
        ])
    );
};

export const hasNonEmptyClipProvidersValues = (clipProviders = {}) => {
    return Object.values(clipProviders).some((clipProviderId) => !!clipProviderId);
};

export const resolveClipProvidersDefaults = (formClipProviders = {}, fallbackDefaults = {}) => {
    return hasNonEmptyClipProvidersValues(formClipProviders)
        ? formClipProviders
        : fallbackDefaults;
};

export const formatClipProvidersForSubmit = (values = {}) => {
    return Object.keys(values.clip_providers ?? {})
        .filter((eventContentTypeId) => values.clip_providers[eventContentTypeId])
        .map((eventContentTypeId) => ({
            clip_provider: convertToInt(values.clip_providers[eventContentTypeId]),
            event_content_type: convertToInt(eventContentTypeId),
        }));
};

export const getDefaultClipProvidersValues = (eventContentTypes = [], defaultDropdownValue = '') => {
    if (!defaultDropdownValue) {
        return {};
    }

    return Object.fromEntries(
        eventContentTypes.map((eventContentType) => [String(eventContentType.id), String(defaultDropdownValue)])
    );
};

export const setClipProvidersInForm = (
    setFormValue,
    eventContentTypes = [],
    defaultDropdownValues = {}
) => {
    if (!setFormValue) {
        return;
    }

    const nextValues = Object.fromEntries(
        eventContentTypes.map(({id}) => {
            const eventContentTypeId = String(id);

            return [eventContentTypeId, defaultDropdownValues[eventContentTypeId] ?? ''];
        })
    );

    setFormValue('clip_providers', nextValues);
};

export const getEventContentTypesFromClipConfigOptions = async (runApolloRequest, tournamentIds = [], sportIds = []) => {
    if (0 === tournamentIds.length && 0 === sportIds.length) {
        return [];
    }

    const {data} = await runApolloRequest('query', {
        fetchPolicy: 'network-only',
        query: clipConfigOptions,
        variables: {
            preferredClipConfigFilters: {tournaments: tournamentIds},
            fallbackClipConfigsFilters: {sports: sportIds, tournamentsEmpty: true},
        },
    });

    return extractEventContentTypes(data?.preferredClipConfigs, data?.fallbackClipConfigs, tournamentIds);
};

export function updateClipProvidersFieldBasedOnPreset(
    props,
    prevFormValues,
    setField,
    state,
    presetsListObject = {},
    {setFormValue = null} = {}
) {
    const {coverage, event_preset} = props.formValues;
    const normalizePresetId = presetId => (null === presetId || undefined === presetId ? null : String(presetId));
    const hasPresetClipProviders = preset => 0 < (_get(preset, 'event.clip_providers', []).length || 0);
    const clipProvidersFieldProps = _get(state, 'defaultForm_fields.clip_providers.props', {});
    const effectiveEventContentTypes = 0 < (_get(state, 'eventContentTypes', []).length || 0)
        ? state.eventContentTypes
        : _get(clipProvidersFieldProps, 'eventContentTypes', []);
    const effectiveClipProvidersOptions = 0 < (_get(state, 'clipProvidersOptions', []).length || 0)
        ? state.clipProvidersOptions
        : _get(clipProvidersFieldProps, 'options', []);
    const selectedPresetId = normalizePresetId(event_preset);
    const coverageChanged = !_isEqual(props.formValues.coverage, prevFormValues.coverage);
    const hasCoverageNow = 0 < coverage.length;
    const shouldClipProvidersFieldBeHidden = 0 === coverage.length;

    const presetChanged = event_preset !== prevFormValues.event_preset;

    if (coverageChanged || (presetChanged && hasCoverageNow && !shouldClipProvidersFieldBeHidden)) {
        setField('clip_providers', {
            hidden: shouldClipProvidersFieldBeHidden,
            options: effectiveClipProvidersOptions,
            eventContentTypes: effectiveEventContentTypes,
        });
    }

    if (!shouldClipProvidersFieldBeHidden
        && hasCoverageNow
        && (event_preset !== prevFormValues.event_preset || (event_preset && coverageChanged))) {
        const selectedPresetFromGraphQl = props.GraphQLOptionsData?.eventPresets?.find(
            preset => normalizePresetId(preset.id) === selectedPresetId
        ) || null;
        const selectedPresetFromCache = Object.values(presetsListObject)?.find(
            preset => normalizePresetId(preset.id) === selectedPresetId
        ) || null;
        let selectedPreset = selectedPresetFromGraphQl || selectedPresetFromCache;

        if (hasPresetClipProviders(selectedPresetFromGraphQl)) {
            selectedPreset = selectedPresetFromGraphQl;
        } else if (hasPresetClipProviders(selectedPresetFromCache)) {
            selectedPreset = selectedPresetFromCache;
        }

        if (Array.isArray(selectedPreset?.event?.products) && 0 === selectedPreset.event.products.length && presetChanged) {
            setField('clip_providers', {
                hidden: true,
                defaultDropdownValues: {},
                options: effectiveClipProvidersOptions,
                eventContentTypes: effectiveEventContentTypes,
            });
            setFormValue && setFormValue('clip_providers', {});

            return;
        }

        let clipProviderFormPropertyLicence = null;

        if (!props.formValues.id) {
            if (state.rightsScopes && state.coverageProducts) {
                clipProviderFormPropertyLicence = getClipProviderFromPropertyLicence(state.rightsScopes, state.coverageProducts);
            }

            if (state?.coverageOptions) {
                clipProviderFormPropertyLicence = state?.coverageOptions?.[0]?.providers?.[0]?.toString();
            }
        }

        const formClipProviders = normalizeClipProvidersValues(props.formValues.clip_providers ?? {});
        const presetDefaults = selectedPreset
            ? getClipProvidersDefaultsFromEvent(selectedPreset.event)
            : {};
        const fallbackDefaults = getDefaultClipProvidersValues(effectiveEventContentTypes, clipProviderFormPropertyLicence || '');

        const defaultDropdownValues = Object.fromEntries(
            (effectiveEventContentTypes || []).map(({id}) => {
                const eventContentTypeId = String(id);

                return [
                    eventContentTypeId,
                    (!presetChanged && formClipProviders[eventContentTypeId])
                    || presetDefaults[eventContentTypeId]
                    || fallbackDefaults[eventContentTypeId]
                    || '',
                ];
            })
        );

        setField('clip_providers', {
            defaultDropdownValues,
        });

        setClipProvidersInForm(setFormValue, effectiveEventContentTypes, defaultDropdownValues);
    }
}

/**
 * Hide all information about match details
 * and hide checkbox
 * @param setField
 * @param setState
 * @param message
 * @param isLookingForMatch
 */

export const clearMatchDetailsAndDisableLinking = (setField, setState, message, isLookingForMatch = false) => {
    const matchNotFound = {isMatchFound: false};

    setField(['start_time', 'competitor', 'tournament', 'court_venue', 'tv_channels'], {
        values: matchNotFound,
    });

    setField('synchronise_times', {hidden: true});

    setField('message', {
        values: {
            ...matchNotFound,
            message: message,
            loading: isLookingForMatch,
        },
    });

    setState(() => ({
        isLinkingDisabled: true,
    }));
};

export const hasArgosJobScheduling = (propertyLicences, licenceId) => {
    const pl = propertyLicences.find(p => convertToInt(p.id) === convertToInt(licenceId));

    return pl?.property_licence_tech_setup?.has_argos_job_scheduling ?? pl?.has_argos_job_scheduling;
};

export const hasSpiideoIntegration = (propertyLicences, licenceId) => {
    const pl = propertyLicences.find(p => convertToInt(p.id) === convertToInt(licenceId));

    return pl?.property_licence_tech_setup?.has_spiideo_integration ?? pl?.has_spiideo_integration;
};
